// Small shared building blocks for the web hub pages (tabs layout, cards, stats, loaders, inputs).
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

export const inp = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
export const gbp = (v) => "£" + Number(v || 0).toFixed(2);
export const when = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
export const day = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

/** GET + reload, never throws. */
export function useLoad(path, deps = []) {
  const [data, setData] = useState(undefined);
  const load = useCallback(() => { if (!path) { setData(null); return Promise.resolve(); } return api(path).then(setData).catch(() => setData(null)); }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, load, setData];
}

/** POST/PUT/PATCH/DELETE with a toast; returns the response or null. */
export async function act(path, method = "POST", body, ok) {
  try { const r = await api(path, { method, body }); if (ok) toast.success(ok); return r ?? true; } catch (e) { toast.error(e.message || "Something went wrong"); return null; }
}

export const Card = ({ title, children, right, className = "" }) => (
  <section className={"rounded-2xl border p-5 " + className}>
    {title || right ? <div className="mb-3 flex items-center gap-2">{title ? <h2 className="flex-1 font-bold">{title}</h2> : null}{right}</div> : null}
    {children}
  </section>
);
export const Stat = ({ k, v }) => <div className="rounded-2xl border p-4"><p className="text-xs text-muted-foreground">{k}</p><p className="text-2xl font-bold">{v ?? 0}</p></div>;
export const Empty = ({ children }) => <p className="text-sm text-muted-foreground">{children}</p>;
export const Loading = () => <Skeleton className="h-40 rounded-2xl" />;
export const Avatar = ({ src, name, size = 9 }) => src ? <img src={src} alt="" className={"rounded-full object-cover h-" + size + " w-" + size} /> : <span className={"grid place-items-center rounded-full bg-muted text-sm font-bold h-" + size + " w-" + size}>{(name || "?")[0]}</span>;

/** Left-nav tabs page with #hash memory. tabs: [[key, label, Icon, Component]] */
export function TabsPage({ title, sub, tabs, testid }) {
  const [tab, setTab] = useState(() => (window.location.hash || "").replace("#", "") || tabs[0][0]);
  const pick = (k) => { setTab(k); window.history.replaceState(null, "", "#" + k); };
  const cur = tabs.find((t) => t[0] === tab) || tabs[0];
  const Body = cur[3];
  useEffect(() => { document.title = title + " · Fable+"; }, [title]);
  return (
    <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid={testid}>
      <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
      {sub ? <p className="mt-1 text-sm text-muted-foreground">{sub}</p> : null}
      <div className="mt-6 grid gap-6 md:grid-cols-[210px_minmax(0,1fr)]">
        <nav aria-label={title + " sections"} className="flex gap-1 overflow-x-auto md:flex-col">
          {tabs.map(([k, label, Icon]) => <button key={k} type="button" onClick={() => pick(k)} aria-current={cur[0] === k ? "page" : undefined}
            className={"flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold " + (cur[0] === k ? "bg-primary text-primary-foreground" : "hover:bg-muted")}>{Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}{label}</button>)}
        </nav>
        <div className="min-w-0 space-y-5"><Body /></div>
      </div>
    </main>
  );
}
